"use client";

import { CHART_COLORS } from "@/components/charts/event-chart";
import { ChevronRightIcon } from "@/components/event-detail/chevron-right-icon";
import { OrderBookModal } from "@/components/platform/order-book-modal";
import { OrderBook } from "@/components/trade-panel/components/order-book";
import { useTradePanel } from "@/components/trade-panel/hooks/use-trade-panel";
import { SmartImage } from "@/components/ui/smart-image";
import { cn } from "@/lib/utils";
import { useEventConnect } from "@/store/eventConnect";
import { useUserInfo } from "@/store/user-info";
import { formatCentPriceAsChancePercent, resolveUnitPrice } from "@/utils/price";
import { navigateToOrderGuessDetail } from "@/utils/router";
import { useAppRouter } from "@/utils/router";
import { formatUnitPrice, get, getOptionLabel } from "@/utils/utils";
import { useQueryClient } from "@tanstack/react-query";
import { usePathname, useSearchParams } from "next/navigation";
import { useCallback, useEffect, useRef, useState } from "react";

const REFRESH_SPIN_MS = 1000;
import { type TabType } from "./guess-expand-content";
import { MultiGuessPositionsSections } from "./multi-guess-positions-sections";
import { OrderHistorySheet } from "./order-history-sheet";
import { PositionsPanel } from "./positions-panel";
import { usePositionsData } from "./positions-panel-hooks/use-positions-data";

interface GuessCardProps {
    guess: any;
    index: number;
    event: any;
    /** 是否为单个竞猜 */
    isSingle?: boolean;
    /** 是否为预览模式（禁用交易功能） */
    isPreview?: boolean;
    isLast?: boolean;
    /** 当前选中的竞猜编号 */
    selectedGuessNo?: string | null;
    /** 当前选中的选项类型（1=Yes, 2=No） */
    selectedBetType?: 1 | 2 | null;
}

export function GuessCard(
    {
        guess,
        index,
        event,
        isSingle = false,
        isPreview = false,
        isLast = false,
        selectedGuessNo = null,
        selectedBetType = null,
    }: GuessCardProps,
) {
    const [ orderSheetOpen, setOrderSheetOpen ] = useState( false );
    const { open } = useTradePanel();
    const queryClient = useQueryClient();
    const pathname = usePathname();
    const router = useAppRouter();
    const [ orderBookTab, setOrderBookTab ] = useState<TabType>( "trade" );
    const [ isOrderBookModalOpen, setIsOrderBookModalOpen ] = useState( false );
    const { userId } = useUserInfo();
    const searchParams = useSearchParams();
    const eventNo = searchParams.get( "eventno" );

    // 获取 WebSocket 实时市场价格
    const guessNo = get( guess, "guessNo", "" );
    const marketPrice = useEventConnect( state => state.guesses[guessNo] );
    const loadSnapshot = useEventConnect( state => state.loadSnapshot );
    const [ isRefreshingOrderBook, setIsRefreshingOrderBook ] = useState( false );
    const refreshOrderBookTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>( null );

    const guessColor = CHART_COLORS[index % CHART_COLORS.length];

    const handleRefreshOrderBook = useCallback( () => {
        if ( !guessNo || isRefreshingOrderBook ) return;
        if ( refreshOrderBookTimeoutRef.current ) clearTimeout( refreshOrderBookTimeoutRef.current );
        setIsRefreshingOrderBook( true );
        loadSnapshot( guessNo, { force: true } );
        refreshOrderBookTimeoutRef.current = setTimeout( () => {
            refreshOrderBookTimeoutRef.current = null;
            setIsRefreshingOrderBook( false );
        }, REFRESH_SPIN_MS );
    }, [ guessNo, isRefreshingOrderBook, loadSnapshot ] );

    useEffect( () => () => {
        if ( refreshOrderBookTimeoutRef.current ) clearTimeout( refreshOrderBookTimeoutRef.current );
    }, [] );

    // 判断事件是否已结束：检查 status 字段，status 为 2（暂停）或 3（结束）时视为已结束
    const status = get( guess, "status", 0 );
    const isEnded = status === 2 || status === 3;

    // 判断是否选中
    const isSelected = selectedGuessNo === guessNo;
    const isYesSelected = isSelected && selectedBetType === 1;
    const isNoSelected = isSelected && selectedBetType === 2;

    // 计算百分比（使用 WebSocket 实时价格或回退到初始价格）
    const yesRealtimePrice = resolveUnitPrice( marketPrice?.yesPrice ?? get( guess, "yesRealtimePrice" ) );
    const noRealtimePrice = resolveUnitPrice( marketPrice?.noPrice ?? get( guess, "noRealtimePrice" ) );
    const yesPercent = formatCentPriceAsChancePercent( yesRealtimePrice );

    // 用户挂单数量
    const userPendingOrderCount = get( guess, "userPendingOrderCount", 0 );
    const hasPendingOrders = userPendingOrderCount > 0;

    const { positionItems } = usePositionsData( guessNo, userId, isSingle );
    const yesPosition = positionItems.find( ( item ) => item.guessType === 1 );
    const noPosition = positionItems.find( ( item ) => item.guessType === 2 );

    // 持仓数量
    const userYesShareQuantity = isSingle ? get( yesPosition, "shares", 0 ) : get( guess, "userYesShareQuantity", 0 );
    const userNoShareQuantity = isSingle ? get( noPosition, "shares", 0 ) : get( guess, "userNoShareQuantity", 0 );

    // 获取平均价格（如果有持仓）
    const yesAvgPrice = userYesShareQuantity > 0 && yesPosition?.price ? yesPosition.price : null;
    const noAvgPrice = userNoShareQuantity > 0 && noPosition?.price ? noPosition.price : null;

    // 用于标签显示的价格（优先使用平均价格，否则使用实时价格）
    const yesDisplayPrice = yesAvgPrice ?? yesRealtimePrice;
    const noDisplayPrice = noAvgPrice ?? noRealtimePrice;

    // 判断是否显示持仓标签
    const showYesPosition = userYesShareQuantity > 0 && yesRealtimePrice;
    const showNoPosition = userNoShareQuantity > 0 && noRealtimePrice;

    const refreshHoldings = useCallback( () => {
        if ( !guessNo ) return;
        const targetEventNo = get( event, "eventNo", "" );
        queryClient.invalidateQueries( { queryKey: [ "guess-trade-list", guessNo ] } );
        queryClient.invalidateQueries( { queryKey: [ "guess-open-orders", guessNo ] } );
        queryClient.invalidateQueries( { queryKey: [ "guess-trade-history", guessNo ] } );
        queryClient.invalidateQueries( { queryKey: [ "eventDetail", "byGuess", guessNo ] } );
        queryClient.invalidateQueries( { queryKey: [ "eventChartData", "guess", guessNo ] } );
        queryClient.invalidateQueries( { queryKey: [ "userGuessOrder", guessNo ] } );
        if ( targetEventNo ) {
            queryClient.invalidateQueries( { queryKey: [ "eventDetail", "byEvent", targetEventNo ] } );
            queryClient.invalidateQueries( { queryKey: [ "eventChartData", "event", targetEventNo ] } );
        }
    }, [ guessNo, event, queryClient ] );

    const handleOpenTradeModal = ( selectedOption: "yes" | "no" ) => {
        // Disable trading in preview mode or when ended
        if ( isPreview || isEnded ) return;

        if ( isSingle ) {
            setOrderBookTab( selectedOption === "yes" ? "trade-yes" : "trade-no" );
        }

        open( {
            eventInfo: event,
            guessInfo: guess,
            selectedOption,
            tradeType: "buy",
            pathname,
            onSuccess: refreshHoldings,
        } );
    };

    // Handle clicking on open order text
    const handleOpenOrderClick = useCallback( ( e: React.MouseEvent ) => {
        e.stopPropagation();
        setOrderSheetOpen( true );
    }, [] );

    // Handle clicking on clock icon in orderbook
    const handleOpenOrderHistory = useCallback(
        ( _guessNo: string, _direction: number, _betType: number, _price: number ) => {
            setOrderSheetOpen( true );
        },
        [],
    );

    return (
        <div
            id={`guess-${get( guess, "guessNo", "" )}`}
            className={cn(
                isSingle ? "border-b border-stroke" : "rounded-xl border border-stroke bg-surface-card ",
                isSingle && isLast && "border-b-0",
                !isSingle && !isLast && "mb-3",
                isEnded && "opacity-40",
            )}
        >
            {!isSingle && (
                <div className="flex flex-col gap-3 p-3">
                    <div
                        className="flex h-11 w-full cursor-pointer items-center gap-2"
                        onClick={() =>
                            navigateToOrderGuessDetail( router, {
                                guessNo: guess.guessNo,
                                eventNo: eventNo || get( event, "eventNo", "" ),
                            } )}
                    >
                        <div className="flex min-w-0 flex-1 items-center gap-2">
                            {get( guess, "imageUrl", false ) && (
                                <SmartImage
                                    src={get( guess, "imageUrl", "" )}
                                    alt={guess.title}
                                    className="size-11 shrink-0 rounded-lg object-cover"
                                    imageUrlOptions={{ width: 150, height: 150 }}
                                />
                            )}

                            <div className="flex min-h-0 min-w-0 flex-1 flex-col justify-center gap-1">
                                <span className="line-clamp-2 break-words text-[16px] font-bold leading-5 text-t1">
                                    {guess.title}
                                </span>
                            </div>
                        </div>
                        <div className="flex shrink-0 items-center">
                            <span className="text-base font-semibold leading-none tracking-tight text-t1">
                                {yesPercent}
                            </span>
                        </div>
                    </div>

                    {!isEnded && (
                        <div className="flex w-full gap-2">
                            <button
                                onClick={( e ) => {
                                    e.stopPropagation();
                                    handleOpenTradeModal( "yes" );
                                }}
                                disabled={isPreview}
                                className={cn(
                                    "flex h-10 min-w-0 flex-1 items-center justify-center gap-1 rounded-lg px-2 text-sm font-semibold transition-all",
                                    isPreview
                                        ? "cursor-not-allowed opacity-40"
                                        : "active:scale-95 active:bg-yes active:text-content-fixed-inverse",
                                    isYesSelected ? "bg-yes text-content-fixed-inverse" : "bg-yes-subtle text-yes",
                                )}
                            >
                                <span className="truncate">{getOptionLabel( "yes", guess, event )}</span>
                                <span className="shrink-0">
                                    {formatUnitPrice( yesRealtimePrice, { precision: 1 } )}
                                </span>
                            </button>
                            <button
                                onClick={( e ) => {
                                    e.stopPropagation();
                                    handleOpenTradeModal( "no" );
                                }}
                                disabled={isPreview}
                                className={cn(
                                    "flex h-10 min-w-0 flex-1 items-center justify-center gap-1 rounded-lg px-2 text-sm font-semibold transition-all",
                                    isPreview
                                        ? "cursor-not-allowed opacity-40"
                                        : "active:scale-95 active:bg-no active:text-content-fixed-inverse",
                                    isNoSelected ? "bg-no text-content-fixed-inverse" : "bg-no-subtle text-no",
                                )}
                            >
                                <span className="truncate">{getOptionLabel( "no", guess, event )}</span>
                                <span className="shrink-0">{formatUnitPrice( noRealtimePrice, { precision: 1 } )}</span>
                            </button>
                        </div>
                    )}
                </div>
            )}

            {/* My Transactions（单竞猜时，Order Book 上方） */}
            {isSingle && (
                <div
                    className="rounded-xl border border-stroke bg-surface-card px-3 h-12"
                    onClick={() =>
                        navigateToOrderGuessDetail( router, {
                            guessNo,
                            eventNo: eventNo || get( event, "eventNo", "" ),
                        } )}
                >
                    <button type="button" className={cn( "flex w-full items-center justify-between py-3" )}>
                        <span className="flex items-center gap-1">
                            <span className="text-base font-medium text-t1">My Transactions</span>
                        </span>
                        <ChevronRightIcon className="w-6 h-6 text-t3 transition-transform duration-200" />
                    </button>
                </div>
            )}
            {isSingle && (
                <OrderBook
                    guessInfo={guess}
                    eventNo={eventNo}
                    defaultExpanded={false}
                    className="mt-3 border border-stroke rounded-xl px-3 py-2 gap-2 bg-surface-card"
                >
                </OrderBook>
            )}

            {isSingle && <PositionsPanel event={event} guessNo={guessNo} />}

            {!isSingle && !isPreview && <MultiGuessPositionsSections guess={guess} event={event} />}

            {/* Order History Sheet */}
            <OrderHistorySheet
                open={orderSheetOpen}
                onOpenChange={setOrderSheetOpen}
                guessNo={guessNo}
                event={event}
                guess={guess}
            />
            <OrderBookModal isOpen={isOrderBookModalOpen} onClose={() => setIsOrderBookModalOpen( false )}>
            </OrderBookModal>
        </div>
    );
}
